import '@firebase/auth';
import { v4 as uuidV4 } from 'uuid';
import { Machine, MachineConfig } from 'xstate';

import { options } from 'src/modules/designStudio/machines/history/options';

import { EmptyDesignConfig } from '../../constants';
import type { Context, Event, InitialContext, Schema } from '../types';

export const initialContext = {
  email: null,
  userId: null,
  firebaseApp: null,
  buildingModel: null,
  jobId: null,
  history: null,
  currentVersion: null,
  designConfig: { ...EmptyDesignConfig },
  currentIndexInHistory: undefined,
  canUndo: false,
  canRedo: false,
};

const createMachineConfig = (
  jobId: number | null,
  authToken?: string | null,
  shareToken?: string,
): MachineConfig<Context, Schema, Event> => ({
  id: 'history',
  context: {
    ...initialContext,
    sessionId: uuidV4(),
    jobId,
    authToken,
    shareToken,
    canRedo: false,
    canUndo: false,
  },
  initial: 'setup',
  states: {
    setup: {
      invoke: {
        src: 'setupAndFetchModelReference',
        onDone: [
          {
            target: 'afterSetup',
            actions: ['setupCompletion'],
            cond: 'isShareView',
          },
          {
            target: 'fetchCurrentVersion',
            actions: ['setupCompletion'],
          },
        ],
        onError: {
          target: 'afterSetup',
          actions: 'logError',
        },
      },
    },
    fetchCurrentVersion: {
      invoke: {
        src: 'fetchCurrentVersion',
        onDone: {
          target: 'afterSetup',
        },
        onError: {
          target: 'afterSetup',
          actions: 'logError',
        },
      },
    },
    afterSetup: {
      type: 'parallel',
      on: {
        UPDATE_UNDO_REDO_STATE: {
          actions: 'updateCanUndoAndRedo',
        },
      },
      states: {
        listeningForNewVersions: {
          invoke: {
            src: 'fetchVersions',
            onError: {
              actions: 'logError',
            },
          },
          on: {
            SET_HISTORY: {
              actions: ['setHistory', 'updateUndoRedoState'],
            },
          },
        },
        waitingForDesignChanges: {
          initial: 'idle',
          states: {
            updatingDesign: {
              invoke: {
                src: 'createVersion',
                onDone: 'idle',
                onError: {
                  target: 'idle',
                  actions: 'logError',
                },
              },
            },
            undoDesign: {
              invoke: {
                src: 'markPreviousDesignAsCurrent',
                onDone: 'idle',
                onError: {
                  target: 'idle',
                  actions: 'logError',
                },
              },
            },
            redoDesign: {
              invoke: {
                src: 'markNextDesignAsCurrent',
                onDone: 'idle',
                onError: {
                  target: 'idle',
                  actions: 'logError',
                },
              },
            },
            idle: {
              on: {
                UNDO_DESIGN_CHANGE: {
                  target: 'undoDesign',
                  cond: 'canUndo',
                  // these are optimistic operations
                  actions: 'undoDesignAction',
                },
                REDO_DESIGN_CHANGE: {
                  target: 'redoDesign',
                  cond: 'canRedo',
                  // these are optimistic operations
                  actions: 'redoDesignAction',
                },
                UPDATE_DESIGN: {
                  target: 'updatingDesign',
                  actions: ['updateDesignConfigOptimistically', 'trackUpdate'],
                },
                RESET_DESIGN: {
                  target: 'updatingDesign',
                  actions: ['updateDesignConfigOptimistically'],
                },
              },
            },
          },
        },
      },
    },
  },
  exit: 'teardownFirebaseApp',
});

const createMachine = ({ jobId, authToken, shareToken }: InitialContext) =>
  Machine<Context, Schema, Event>(
    createMachineConfig(jobId, authToken, shareToken),
    options,
  );

export { createMachine, createMachineConfig };
